import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { and } from 'ember-truth-helpers';
import GeneralButtonsButtonDayLiveshop from 'lumen/components/general/buttons/button-day-liveshop';
import type { Parity, Period } from 'lumen/models';
import formatDate from 'lumen/utils/format-local-date';

interface GeneralTablesCellsCellDateOverviewSignature {
  Args: {
    hasDayLiveShop?: boolean;
    parity?: Parity;
    period?: Period;
  };
}

export default class GeneralTablesCellsCellDateOverview extends Component<GeneralTablesCellsCellDateOverviewSignature> {
  @service declare intl: Services['intl'];
  @service declare liveupdate: Services['liveupdate'];

  @tracked isLiveupdateTriggered: boolean = false;

  get dateFormatted(): string {
    const { period } = this.args;

    if (period?.day?.date) {
      return formatDate(period.day.date);
    }

    return this.intl.t('general.missing');
  }

  get isDayLiveupdateTriggerAllowed(): boolean {
    return !this.isUpdating;
  }

  get isDayLiveupdateTriggerVisible(): boolean {
    return this.isLiveupdateTriggered && !this.isPartialLiveupdateOngoing;
  }

  get isPartialLiveupdateOngoing(): boolean {
    const { partialLiveupdatesForThisSelection } = this.liveupdate;

    if (!partialLiveupdatesForThisSelection) {
      return false;
    }

    return partialLiveupdatesForThisSelection.length > 0;
  }

  get isUpdating(): boolean {
    const { parity, period } = this.args;
    const fromDate = period?.fromDate;
    const currentDate = period?.day?.date;

    if (!fromDate || !currentDate || !parity) {
      return false;
    }

    return this.liveupdate.isSingleDefaultParityUpdating(
      fromDate,
      currentDate,
      parity,
    );
  }

  @action startDayLiveupdate(): void {
    const { period } = this.args;

    this.liveupdate.startLiveParityShop({
      dayShop: period?.day?.date.format('YYYY-MM-DD'),
    });
  }

  @action updateIsLiveupdateTriggered(value: boolean): void {
    this.isLiveupdateTriggered = value;
  }

  <template>
    {{! template-lint-disable require-presentational-children }}
    <div
      class="cell-wrapper cell-wrapper--parity-date cell-wrapper--date flex items-center"
      role="button"
      {{on "mouseenter" (fn this.updateIsLiveupdateTriggered true)}}
      {{on "mouseleave" (fn this.updateIsLiveupdateTriggered false)}}
    >
      <span class="pos-arrival-date">
        {{this.dateFormatted}}
      </span>

      <GeneralButtonsButtonDayLiveshop
        @isDayLiveupdateLimitReached={{false}}
        @isUpdating={{this.isUpdating}}
        @isVisible={{and
          @hasDayLiveShop
          this.isDayLiveupdateTriggerAllowed
          this.isDayLiveupdateTriggerVisible
        }}
        @startDayLiveupdate={{this.startDayLiveupdate}}
      />
    </div>
  </template>
}
